import React, { useState, useEffect } from 'react';
import { API } from 'aws-amplify';
import { useSelector } from 'react-redux';
import qs from 'querystring';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import CardContent from '@material-ui/core/CardContent';
import Typography from '@material-ui/core/Typography';
import Spin from 'components/common/Spin';
import DeleteIcon from '@material-ui/icons/DeleteOutlined';
import Box from '@material-ui/core/Box';
import Paper from '@material-ui/core/Paper';
import Link from '@material-ui/core/Link';
import DatePickerLabel from 'components/common/DatePickerLabel';
import TextField from '@material-ui/core/TextField';
import IconButton from '@material-ui/core/IconButton';
import SendIcon from '@material-ui/icons/Send';
import { useMutation, Query } from 'react-apollo';
import {
  ADD_COMMENT,
  GET_COMMENTS,
  UPDATE_SCHOOL,
} from 'graphql/student_college';
import useSnack from 'hooks/useSnack';
import { toUserTimezone } from 'utils';
import CloseIcon from '@material-ui/icons/Close';
import withConfirm from 'hooks/withConfirm';
import Summary from './components/Summary';
import Costs from './components/Costs';
import AidAndDebt from './components/AidAndDebt';
import Retention from './components/Retention';
import Salary from './components/Salary';
import StudentBody from './components/StudentBody';
import TestScores from './components/TestScores';
import STATUSES from 'constant/college-application-status';
import ADMISSION_TYPES from 'constant/admission-types';
import Dropdown from 'components/form/Dropdown';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import Button from 'components/common/Button';

const NavTab = withStyles((theme) => ({
  root: {
    minWidth: 72,
  },
}))(Tab);

const useStyles = makeStyles((theme) => ({
  card: {
    'width': 'auto',
    'minWidth': 600,
    'maxWidth': 900,
    'height': '100vh',
    'padding': 16,
    'paddingLeft': theme.spacing(3),
    'display': 'flex',
    'flexDirection': 'column',
    '& table': {
      marginBottom: 16,
    },
    '& .MuiChip-root': {
      borderRadius: 4,
      backgroundColor: '#EFF2F5',
      height: 24,
    },
    [theme.breakpoints.down('sm')]: {
      minWidth: '100vw',
      width: '100vw',
    },
  },
  media: {
    height: 140,
  },
  title: {
    fontSize: 14,
  },
  pos: {
    marginBottom: 12,
  },
  textField: {
    '& .MuiInputBase-root': {
      borderRadius: 30,
      paddingRight: 40,
      paddingLeft: 16,
    },
  },
  btn: {
    position: 'absolute',
    right: 10,
  },
  closeIcon: {
    position: 'relative',
    float: 'right',
  },
  icon: {
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(1),
  },
  label: {
    fontSize: 10,
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
    fontWeight: 700,
    marginBottom: theme.spacing(1),
    letterSpacing: 1,
  },
  collegeName: {},
}));

const School = ({
  onDelete,
  collegeId,
  school,
  location,
  onClose,
  confirm,
}) => {
  const query = qs.parse(location.search.substr(1));
  const user = useSelector((s) => s.user.user);
  const snack = useSnack();
  const classes = useStyles();
  const [college, setCollege] = useState();
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(query.tab ? query.tab : '1');
  const [saving, setSaving] = useState(false);
  const [comment, setComment] = useState('');
  const [addComment] = useMutation(ADD_COMMENT);
  const [updateSchool] = useMutation(UPDATE_SCHOOL);
  const canEdit = user.type !== 'parent';

  useEffect(() => {
    API.get('api', `/college/${collegeId}`).then((data) => {
      setCollege(data);
      setLoading(false);
    });
  }, [collegeId]);

  const onSaveComment = () => {
    setSaving(true);
    addComment({
      variables: {
        data: {
          comment,
          user_id: user.id,
          student_college_id: school.id,
        },
      },
      update: (proxy, mutationResult) => {
        const data = proxy.readQuery({
          query: GET_COMMENTS,
          variables: { schoolId: school.id },
        });
        if (data) {
          data.rows.push(mutationResult.data.row);
        }
        proxy.writeQuery({
          query: GET_COMMENTS,
          variables: { schoolId: school.id },
          data: {
            rows: data ? data.rows : [mutationResult.data.row],
          },
        });
      },
    })
      .then(() => {
        setComment('');
      })
      .catch((e) => snack.error(e.message))
      .finally(() => setSaving(false));
  };

  const handleDelete = () => {
    confirm(() => onDelete(school.id));
  };

  const onDateChange = (date) => {
    updateSchool({
      variables: {
        id: school.id,
        input: {
          app_deadline: date.format('YYYY-MM-DD'),
        },
      },
    });
  };

  const onStatusChange = (val) => {
    updateSchool({
      variables: {
        id: school.id,
        input: {
          status: val,
        },
      },
    });
  };

  const onAdmissionTypeChange = (val) => {
    updateSchool({
      variables: {
        id: school.id,
        input: {
          admission_type: val,
        },
      },
    });
  };

  return (
    <div className={classes.card}>
      {loading ? (
        <CardContent>
          <Spin spinning linear />
        </CardContent>
      ) : (
        college &&
        school && (
          <React.Fragment>
            <Box marginBottom={5}>
              <Typography variant="subtitle1" className={classes.collegeName}>
                {college['school.name']}{' '}
                <IconButton onClick={handleDelete}>
                  <DeleteIcon />
                </IconButton>
                <IconButton
                  className={classes.closeIcon}
                  color="inherit"
                  onClick={onClose}
                >
                  <CloseIcon />
                </IconButton>
              </Typography>
              <Box display="flex" alignItems="center" marginBottom={0.5}>
                <LocationOnIcon fontSize="small" className={classes.icon} />
                <Typography variant="caption" color="textSecondary">
                  {college['school.city']}, {college['school.state']}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center">
                <OpenInNewIcon fontSize="small" className={classes.icon} />
                <Link
                  href={`//${college['school.school_url']}`}
                  target="_blank"
                >
                  <Typography variant="caption">
                    {college['school.school_url']}
                  </Typography>
                </Link>
              </Box>
            </Box>
            <Box
              display="flex"
              marginBottom={3}
              justifyContent="space-between"
              flexWrap="wrap"
            >
              <Box>
                <Typography className={classes.label}>App Deadline</Typography>
                <DatePickerLabel
                  value={school.app_deadline}
                  onChange={onDateChange}
                />
              </Box>
              <Box>
                <Typography className={classes.label}>Status</Typography>
                <Dropdown
                  id="status"
                  options={STATUSES}
                  disabled={!canEdit}
                  value={school.status}
                  onChange={(e) => onStatusChange(e)}
                  color={STATUSES.find((s) => s.value === school.status)?.color}
                />
              </Box>
              <Box flexGrow={{ xs: 2, sm: 0 }} marginTop={{ xs: 2, sm: 0 }}>
                <Typography className={classes.label}>
                  Admission Type
                </Typography>
                <Dropdown
                  options={ADMISSION_TYPES}
                  disabled={!canEdit}
                  id="admission_type"
                  value={school.admission_type}
                  onChange={(e) => onAdmissionTypeChange(e)}
                />
              </Box>
            </Box>
            <Divider />
            <Box marginTop={3} marginBottom={3}>
              <Tabs
                value={tab}
                onChange={(e, v) => setTab(v)}
                indicatorColor="primary"
                textColor="primary"
              >
                <NavTab value={'1'} label="College Info" />
                <NavTab value={'2'} label="Activity log" />
              </Tabs>
            </Box>
            {tab === '1' ? (
              <>
                {college && (
                  <Box>
                    <Summary school={college} />
                    <Costs school={college} />
                    <Retention school={college} />
                    <AidAndDebt school={college} />
                    <Salary school={college} />
                    <StudentBody school={college} />
                    <TestScores school={college} />
                  </Box>
                )}
              </>
            ) : (
              <>
                <Query query={GET_COMMENTS} variables={{ schoolId: school.id }}>
                  {({ data, loading }) => {
                    if (loading) return <Spin spinning linear />;
                    const { rows } = data;
                    return (
                      <Box flex={1}>
                        <Box>
                          {rows.map((row) => (
                            <Box key={row.id} marginBottom={3}>
                              <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                              >
                                <Typography variant="body2">
                                  {row.user.user_first_name}
                                </Typography>
                                <Typography variant="caption">
                                  {toUserTimezone(
                                    row.created_at,
                                    user.timezone,
                                  ).format('LLL')}
                                </Typography>
                              </Box>
                              <Typography variant="body1">
                                {row.comment}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    );
                  }}
                </Query>
                <Box flex={1} marginBottom={2}>
                  <Paper>
                    <TextField
                      multiline
                      fullWidth
                      placeholder="Add your comment"
                      margin="none"
                      size="small"
                      variant="outlined"
                      classes={{
                        root: classes.textField,
                      }}
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                    />
                    <Button
                      color="primary"
                      disabled={!comment}
                      size="large"
                      onClick={onSaveComment}
                      className={classes.btn}
                    >
                      Post
                    </Button>
                  </Paper>
                </Box>
              </>
            )}
          </React.Fragment>
        )
      )}
    </div>
  );
};

export default withConfirm(School);
